import {
  Activity, Anchor, Archive, Atom, Axe, BarChart3, Bird, Blocks, Bomb, Bone, BookOpen, Bookmark, Bot, BowArrow, Box, Boxes, Brain, Brush, Bug,
  Building2, Calendar, CalendarDays, Car, Castle, Cat, CheckSquare, Circle, CircleDot, Clock, Cloud, CloudLightning, Clover, Club, Code, Coins,
  Compass, Component, Cpu, Crosshair, Crown, Database, Diamond, Dices, Dog, Droplet, Earth, Eye, Feather, FileText, Film, Fish, Flag, Flame,
  FlaskConical, FlaskRound, Folder, Footprints, Gamepad2, Gauge, Gem, Ghost, Gift, GitBranch, Globe, Hammer, Hand, Hash, Headphones, Heart,
  HeartPulse, Hexagon, Hourglass, Image, Joystick, Kanban, Key, Landmark, Layers, Leaf, Lightbulb, Link, ListTodo, Magnet, Map, Medal,
  MessageSquare, Mic, Milestone, Moon, MoonStar, Mountain, MountainSnow, MoveHorizontal, Music, Orbit, Package, Palette, Paperclip,
  PawPrint, PenTool, Pickaxe, PieChart, Pin, Plane, Puzzle, Rabbit, Rocket, Route, Scroll, ScrollText, Server, Settings, Shapes, Shield, Ship,
  Sigma, Signpost, Skull, Snowflake, Spade, Sparkle, Sparkles, Sprout, Square, SquareCheck, Star, StickyNote, Store, Sun, Sunrise, Sword, Swords,
  Tag, Tags, Target, Telescope, Tent, Terminal, Text, ThumbsUp, Timer, Train, TreePine, Trees, Triangle, Trophy, User, UserRound, Users,
  VenetianMask, Volume2, Vote, WandSparkles, Waves, Waypoints, Wind, Wrench, Zap,
  type LucideIcon,
} from 'lucide-react';
import type { CSSProperties } from 'react';

/**
 * Icons are stored in documents as tokens: `i:<lucide-kebab-name>`. Only the curated set below is
 * bundled (keeps the app small and the picker focused).
 */
export const ICONS: Record<string, LucideIcon> = {
  activity: Activity, anchor: Anchor, archive: Archive, atom: Atom, axe: Axe, 'bar-chart-3': BarChart3, bird: Bird, blocks: Blocks,
  bomb: Bomb, bone: Bone, 'book-open': BookOpen, bookmark: Bookmark, bot: Bot, 'bow-arrow': BowArrow, box: Box, boxes: Boxes, brain: Brain,
  brush: Brush, bug: Bug, 'building-2': Building2, calendar: Calendar, 'calendar-days': CalendarDays, car: Car, castle: Castle, cat: Cat,
  'check-square': CheckSquare, circle: Circle, 'circle-dot': CircleDot, clock: Clock, cloud: Cloud, 'cloud-lightning': CloudLightning,
  clover: Clover, club: Club, code: Code, coins: Coins, compass: Compass, component: Component, cpu: Cpu, crosshair: Crosshair, crown: Crown,
  database: Database, diamond: Diamond, dices: Dices, dog: Dog, droplet: Droplet, earth: Earth, eye: Eye, feather: Feather,
  'file-text': FileText, film: Film, fish: Fish, flag: Flag, flame: Flame, 'flask-conical': FlaskConical, 'flask-round': FlaskRound,
  folder: Folder, footprints: Footprints, 'gamepad-2': Gamepad2, gauge: Gauge, gem: Gem, ghost: Ghost, gift: Gift, 'git-branch': GitBranch,
  globe: Globe, hammer: Hammer, hand: Hand, hash: Hash, headphones: Headphones, heart: Heart, 'heart-pulse': HeartPulse, hexagon: Hexagon,
  hourglass: Hourglass, image: Image, joystick: Joystick, kanban: Kanban, key: Key, landmark: Landmark, layers: Layers, leaf: Leaf,
  lightbulb: Lightbulb, link: Link, 'list-todo': ListTodo, magnet: Magnet, map: Map, medal: Medal, 'message-square': MessageSquare, mic: Mic,
  milestone: Milestone, moon: Moon, 'moon-star': MoonStar, mountain: Mountain, 'mountain-snow': MountainSnow, 'move-horizontal': MoveHorizontal,
  music: Music, orbit: Orbit, package: Package, palette: Palette, paperclip: Paperclip, 'paw-print': PawPrint, 'pen-tool': PenTool,
  pickaxe: Pickaxe, 'pie-chart': PieChart, pin: Pin, plane: Plane, puzzle: Puzzle, rabbit: Rabbit, rocket: Rocket, route: Route, scroll: Scroll,
  'scroll-text': ScrollText, server: Server, settings: Settings, shapes: Shapes, shield: Shield, ship: Ship, sigma: Sigma, signpost: Signpost,
  skull: Skull, snowflake: Snowflake, spade: Spade, sparkle: Sparkle, sparkles: Sparkles, sprout: Sprout, square: Square,
  'square-check': SquareCheck, star: Star, 'sticky-note': StickyNote, store: Store, sun: Sun, sunrise: Sunrise, sword: Sword, swords: Swords,
  tag: Tag, tags: Tags, target: Target, telescope: Telescope, tent: Tent, terminal: Terminal, text: Text, 'thumbs-up': ThumbsUp, timer: Timer,
  train: Train, 'tree-pine': TreePine, trees: Trees, triangle: Triangle, trophy: Trophy, user: User, 'user-round': UserRound, users: Users,
  'venetian-mask': VenetianMask, 'volume-2': Volume2, vote: Vote, 'wand-sparkles': WandSparkles, waves: Waves, waypoints: Waypoints,
  wind: Wind, wrench: Wrench, zap: Zap,
};

/** Grouping for the icon picker. */
export const ICON_GROUPS: { label: string; icons: string[] }[] = [
  {
    label: 'Game',
    icons: ['swords', 'sword', 'bow-arrow', 'shield', 'axe', 'hammer', 'pickaxe', 'bomb', 'crosshair', 'target', 'skull', 'bone', 'ghost', 'venetian-mask', 'bot', 'bug', 'crown', 'gem', 'coins', 'trophy', 'medal', 'gift', 'key', 'dices', 'gamepad-2', 'joystick', 'heart', 'heart-pulse', 'zap', 'flame', 'snowflake', 'droplet', 'wind', 'sparkles', 'sparkle', 'wand-sparkles', 'flask-conical', 'flask-round', 'hourglass', 'timer', 'scroll', 'book-open', 'map', 'compass', 'flag', 'castle', 'tent', 'landmark', 'package', 'puzzle', 'spade', 'club', 'diamond', 'clover'],
  },
  {
    label: 'World',
    icons: ['mountain', 'mountain-snow', 'trees', 'tree-pine', 'leaf', 'sprout', 'waves', 'earth', 'globe', 'sun', 'moon', 'moon-star', 'sunrise', 'cloud', 'cloud-lightning', 'orbit', 'atom', 'telescope', 'magnet', 'rocket', 'ship', 'plane', 'train', 'car', 'building-2', 'store', 'anchor', 'footprints', 'paw-print', 'rabbit', 'bird', 'fish', 'cat', 'dog', 'feather', 'eye', 'brain'],
  },
  {
    label: 'Work',
    icons: ['box', 'boxes', 'layers', 'blocks', 'component', 'shapes', 'hexagon', 'triangle', 'circle', 'square', 'star', 'lightbulb', 'sticky-note', 'scroll-text', 'file-text', 'folder', 'archive', 'bookmark', 'pin', 'tag', 'tags', 'square-check', 'list-todo', 'kanban', 'calendar-days', 'clock', 'milestone', 'route', 'signpost', 'waypoints', 'users', 'user-round', 'hand', 'message-square', 'image', 'film', 'music', 'headphones', 'mic', 'volume-2', 'palette', 'brush', 'pen-tool', 'bar-chart-3', 'pie-chart', 'activity', 'gauge', 'sigma', 'code', 'terminal', 'cpu', 'database', 'server', 'git-branch', 'wrench', 'settings'],
  },
];

/**
 * Icons replaced emoji, but documents written before that (or synced from an older client) can
 * still hold one. Every emoji the app ever offered maps to the icon that took its place, so those
 * documents keep an icon that means the same thing; anything else falls back to the default.
 */
export const LEGACY_EMOJI_ICONS: Record<string, string> = {
  '⚔️': 'swords', '🗡️': 'sword', '🛡️': 'shield', '🏹': 'bow-arrow', '⛏️': 'pickaxe', '💣': 'bomb', '🎯': 'target',
  '💀': 'skull', '🦴': 'bone', '👾': 'ghost', '🤖': 'bot', '🧙': 'venetian-mask', '🧝': 'venetian-mask', '👋': 'hand',
  '🐉': 'paw-print', '🐺': 'dog', '🦅': 'bird', '🧠': 'brain', '👁️': 'eye', '❤️': 'heart',
  '👑': 'crown', '💎': 'gem', '🪙': 'coins', '🏆': 'trophy', '🗝️': 'key', '📦': 'package', '🧩': 'puzzle',
  '🎲': 'dices', '🎮': 'gamepad-2', '🪄': 'wand-sparkles', '🔮': 'orbit', '🧪': 'flask-conical', '🧂': 'flask-round',
  '⚡': 'zap', '🔥': 'flame', '❄️': 'snowflake', '🌊': 'waves', '🌙': 'moon', '⭐': 'star', '🌋': 'mountain',
  '🌿': 'leaf', '🍀': 'clover', '🍄': 'sprout', '🌲': 'tree-pine', '🥾': 'footprints',
  '📜': 'scroll', '📖': 'book-open', '🗺️': 'map', '🧭': 'compass', '🔭': 'telescope', '🏰': 'castle', '🏳️': 'flag',
  '💡': 'lightbulb', '🎵': 'music', '🎨': 'palette', '🚀': 'rocket', '⏳': 'hourglass', '⚙️': 'settings',
};

/** Keyed without the variation selector, so an emoji resolves whether or not it carries one. */
const legacyIcons: Record<string, string> = Object.fromEntries(
  Object.entries(LEGACY_EMOJI_ICONS).map(([emoji, name]) => [emoji.replace(/\uFE0F/g, ''), name]),
);

/** The icon a stored value names, whether it is a token or a legacy emoji. */
export function iconName(value: string | null | undefined): string | null {
  if (!value) return null;
  if (value.startsWith('i:')) return value.slice(2);
  return legacyIcons[value.replace(/\uFE0F/g, '')] ?? null;
}

/** Normalizes a stored value to a token, so a legacy emoji compares equal to the icon that replaced it. */
export function iconToken(value: string | null | undefined): string | null {
  const name = iconName(value);
  return name ? `i:${name}` : null;
}

export interface IconProps {
  /** `i:swords`, or a legacy emoji (mapped to its icon). */
  token: string | null | undefined;
  size?: number;
  className?: string;
  style?: CSSProperties;
  fallback?: string;
  strokeWidth?: number;
}

/** Renders an icon token. */
export function Icon({ token, size = 16, className, style, fallback = 'i:box', strokeWidth }: IconProps) {
  const C = ICONS[iconName(token) ?? ''] ?? ICONS[iconName(fallback) ?? ''] ?? Box;
  return <C size={size} className={className} style={style} strokeWidth={strokeWidth} aria-hidden="true" />;
}

/** Lucide component by kebab name (field types, UI). */
export function iconByName(name: string): LucideIcon {
  return ICONS[name] ?? Box;
}
